"use client";

import { fetchFile } from "@ffmpeg/util";
import {
	AlertTriangle,
	Upload,
	Video,
	Music,
	Info,
	FileText,
	Clock,
	Monitor,
	Volume2,
	Database,
	Calendar,
	Tag,
	Zap,
	Film,
	Activity,
	Cpu,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import VideoPlayer from "@/components/VideoPlayer";
import { useFFmpeg } from "@/contexts/FFmpegContext";
import { useAccessibility } from "@/hooks/useAccessibility";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FileDropZone } from "@/components/FileDropZone";
import { useTranslations } from "next-intl";

interface MediaFile {
	id: string;
	file: File;
	url: string;
	name: string;
	size: number;
	type: 'video' | 'audio';
}

interface StreamInfo {
	index: number;
	codec_name: string;
	codec_long_name: string;
	codec_type: 'video' | 'audio' | 'subtitle' | 'data';
	codec_tag_string?: string;
	codec_tag?: string;
	width?: number;
	height?: number;
	coded_width?: number;
	coded_height?: number;
	closed_captions?: number;
	has_b_frames?: number;
	channels?: number;
	channel_layout?: string;
	sample_rate?: string;
	sample_fmt?: string;
	bits_per_sample?: number;
	bit_rate?: string;
	duration?: string;
	frame_rate?: string;
	avg_frame_rate?: string;
	pixel_format?: string;
	profile?: string;
	level?: string;
	color_range?: string;
	color_space?: string;
	color_transfer?: string;
	color_primaries?: string;
	chroma_location?: string;
	refs?: number;
	is_avc?: string;
	nal_length_size?: string;
	time_base?: string;
	start_pts?: number;
	start_time?: string;
	duration_ts?: number;
	bits_per_raw_sample?: string;
	nb_frames?: string;
	disposition?: Record<string, 0 | 1>;
	tags?: Record<string, string>;
}

interface FormatInfo {
	filename: string;
	nb_streams: number;
	nb_programs: number;
	format_name: string;
	format_long_name: string;
	start_time?: string;
	duration?: string;
	size?: string;
	bit_rate?: string;
	probe_score?: number;
	tags?: Record<string, string>;
}

interface MediaMetadata {
	format: FormatInfo;
	streams: StreamInfo[];
	chapters?: Array<{
		id: number;
		time_base: string;
		start: number;
		start_time: string;
		end: number;
		end_time: string;
		tags?: Record<string, string>;
	}>;
}

interface FrameAnalysis {
	avg_frame_rate: number;
	duration: number;
	nb_frames: number;
	gop_size: number;
	frames: Array<{
		frame_number: number;
		pict_type: number;
		pkt_size: number;
		pts: number;
		dts: number;
		pos: number;
	}>;
}

interface AudioAnalysis {
	peakLevel: number;
	rmsLevel: number;
	duration: number;
	numberOfChannels: number;
	sampleRate: number;
}

interface MediaCapabilityInfo {
	supported: boolean;
	smooth: boolean;
	powerEfficient: boolean;
}

const PICT_TYPE_LABELS: Record<number, string> = {
	1: 'I',
	2: 'P',
	3: 'B',
	4: 'S',
	5: 'SI',
	6: 'SP',
	7: 'BI',
};

const Metadata = () => {
	const { ffmpegInstance, isLoaded, isLoading, error, getFileMetadata, getFrameAnalysis, loadFFmpeg } = useFFmpeg();
	const t = useTranslations("metadataViewer");

	useEffect(() => {
		loadFFmpeg();
	}, [loadFFmpeg]);

	const { announce, announceError, announceSuccess } = useAccessibility();
	
	const [mediaFile, setMediaFile] = useState<MediaFile | null>(null);
	const [metadata, setMetadata] = useState<MediaMetadata | null>(null);
	const [frameAnalysis, setFrameAnalysis] = useState<FrameAnalysis | null>(null);
	const [audioAnalysis, setAudioAnalysis] = useState<AudioAnalysis | null>(null);
	const [mediaCapability, setMediaCapability] = useState<MediaCapabilityInfo | null>(null);
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [statusLog, setStatusLog] = useState(t("status.initializing"));
	const [progress, setProgress] = useState(0);

	// Update status based on FFmpeg loading state
	useEffect(() => {
		if (error) {
			setStatusLog(error);
		} else if (isLoading) {
			setStatusLog(t("status.loading"));
		} else if (isLoaded) {
			setStatusLog(t("status.ready"));
		} else {
			setStatusLog(t("status.initializingAnalyzers"));
		}
	}, [error, isLoading, isLoaded]);

	// Handle file selection
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		// Validate file type
		if (!file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
			announceError(t("status.invalidFile", { name: file.name }));
			setStatusLog(t("status.invalidFile", { name: file.name }));
			return;
		}

		const fileType = file.type.startsWith('video/') ? 'video' : 'audio';
		
		const newMediaFile: MediaFile = {
			id: `media-${Date.now()}`,
			file,
			url: URL.createObjectURL(file),
			name: file.name,
			size: file.size,
			type: fileType
		};

		// Clean up previous file
		if (mediaFile) {
			URL.revokeObjectURL(mediaFile.url);
		}

		setMediaFile(newMediaFile);
		setMetadata(null);
		setStatusLog(t("status.fileLoaded", { name: file.name, size: (file.size / 1024 / 1024).toFixed(2) }));
		announce(t("status.fileLoadedAnnounce", { type: fileType }), "polite");
	};

	// Analyze media file with dual approach: ffprobe-wasm primary, FFmpeg fallback
	const analyzeMetadata = useCallback(async () => {
		if (!mediaFile) {
			setStatusLog(t("status.selectFile"));
			return;
		}

		setIsAnalyzing(true);
		setProgress(0);
		setMetadata(null);
		setFrameAnalysis(null);
		setAudioAnalysis(null);
		setMediaCapability(null);
		setStatusLog(t("status.startingAnalysis"));
		announce(t("status.analysisStarted"), "polite");

		const inputFileName = `input.${mediaFile.file.name.split(".").pop()}`;

		try {
			setStatusLog(t("status.analyzingFile"));
			setProgress(30);

			// Write file to FFmpeg filesystem for fallback method
			if (ffmpegInstance && isLoaded) {
				await ffmpegInstance.writeFile(inputFileName, await fetchFile(mediaFile.file));
			}

			setStatusLog(t("status.runningAnalysis"));
			setProgress(60);

			// Use the combined method that tries ffprobe-wasm first, then falls back to FFmpeg
			const rawMetadata = await getFileMetadata(mediaFile.file, inputFileName);

			setStatusLog(t("status.processingResults"));
			setProgress(80);

			// Convert the raw metadata to our standardized format
			let parsedMetadata: MediaMetadata;

			if (rawMetadata.format && rawMetadata.streams) {
				// This looks like ffprobe-wasm output (similar to ffprobe JSON format)
				parsedMetadata = {
					format: {
						filename: mediaFile.file.name,
						nb_streams: rawMetadata.format.nb_streams || rawMetadata.streams?.length || 0,
						nb_programs: rawMetadata.format.nb_programs || 0,
						format_name: rawMetadata.format.format_name || 'unknown',
						format_long_name: rawMetadata.format.format_long_name || 'Unknown format',
						duration: rawMetadata.format.duration?.toString() || '0',
						size: rawMetadata.format.size?.toString() || mediaFile.file.size.toString(),
						bit_rate: rawMetadata.format.bit_rate?.toString() || '0',
						start_time: rawMetadata.format.start_time?.toString() || '0',
						tags: rawMetadata.format.tags || {}
					},
					streams: rawMetadata.streams.map((stream: any, index: number): StreamInfo => ({
						index: stream.index !== undefined ? stream.index : index,
						codec_name: stream.codec_name || 'unknown',
						codec_long_name: stream.codec_long_name || 'Unknown codec',
						codec_type: stream.codec_type || 'unknown',
						codec_tag_string: stream.codec_tag_string,
						codec_tag: stream.codec_tag,
						width: stream.width,
						height: stream.height,
						coded_width: stream.coded_width || stream.codec_width,
						coded_height: stream.coded_height || stream.codec_height,
						closed_captions: stream.closed_captions,
						has_b_frames: stream.has_b_frames,
						channels: stream.channels,
						channel_layout: stream.channel_layout,
						sample_rate: stream.sample_rate?.toString(),
						sample_fmt: stream.sample_fmt,
						bits_per_sample: stream.bits_per_sample,
						bit_rate: stream.bit_rate?.toString(),
						duration: stream.duration?.toString(),
						frame_rate: stream.r_frame_rate || stream.avg_frame_rate,
						avg_frame_rate: stream.avg_frame_rate,
						pixel_format: stream.pix_fmt,
						profile: stream.profile,
						level: stream.level?.toString(),
						color_range: stream.color_range,
						color_space: stream.color_space,
						color_transfer: stream.color_transfer,
						color_primaries: stream.color_primaries,
						chroma_location: stream.chroma_location,
						refs: stream.refs,
						is_avc: stream.is_avc,
						nal_length_size: stream.nal_length_size,
						time_base: stream.time_base,
						start_pts: stream.start_pts,
						start_time: stream.start_time?.toString(),
						duration_ts: stream.duration_ts,
						bits_per_raw_sample: stream.bits_per_raw_sample,
						nb_frames: stream.nb_frames?.toString(),
						disposition: stream.disposition,
						tags: stream.tags || {}
					})),
					chapters: rawMetadata.chapters || []
				};
			} else {
				// This is likely our FFmpeg fallback format (just tags)
				// Build a basic structure using HTML5 media API + the tags
				const tempMedia = mediaFile.type === 'video' ? document.createElement('video') : document.createElement('audio');
				tempMedia.src = URL.createObjectURL(mediaFile.file);
				
				await new Promise((resolve, reject) => {
					tempMedia.addEventListener('loadedmetadata', resolve, { once: true });
					tempMedia.addEventListener('error', reject, { once: true });
					tempMedia.load();
				});

				const duration = tempMedia.duration;
				let width = 0, height = 0;
				if (mediaFile.type === 'video') {
					width = (tempMedia as HTMLVideoElement).videoWidth;
					height = (tempMedia as HTMLVideoElement).videoHeight;
				}

				URL.revokeObjectURL(tempMedia.src);

				// Build basic metadata structure
				const streams: StreamInfo[] = [];
				const fileExtension = mediaFile.file.name.split('.').pop()?.toLowerCase() || '';
				
				if (mediaFile.type === 'video' && width > 0 && height > 0) {
					streams.push({
						index: 0,
						codec_name: 'h264',
						codec_long_name: 'H.264 / AVC',
						codec_type: 'video',
						width,
						height,
						duration: duration.toString(),
						frame_rate: '30',
						pixel_format: 'yuv420p'
					});
				}
				
				streams.push({
					index: mediaFile.type === 'video' ? 1 : 0,
					codec_name: fileExtension === 'mp3' ? 'mp3' : 'aac',
					codec_long_name: fileExtension === 'mp3' ? 'MP3' : 'AAC',
					codec_type: 'audio',
					channels: 2,
					channel_layout: 'stereo',
					sample_rate: '48000',
					duration: duration.toString()
				});

				parsedMetadata = {
					format: {
						filename: mediaFile.file.name,
						nb_streams: streams.length,
						nb_programs: 0,
						format_name: fileExtension,
						format_long_name: `${fileExtension.toUpperCase()} format`,
						duration: duration.toString(),
						size: mediaFile.file.size.toString(),
						bit_rate: Math.round((mediaFile.file.size * 8) / duration).toString(),
						tags: rawMetadata // This should be the extracted metadata tags
					},
					streams
				};
			}

			setMetadata(parsedMetadata);
			setProgress(85);

			// Run additional analyses in parallel
			const additionalAnalyses: Promise<void>[] = [];

			// Frame analysis (video files only, requires ffprobe-wasm)
			const hasVideoStream = parsedMetadata.streams.some(s => s.codec_type === 'video');
			if (hasVideoStream) {
				additionalAnalyses.push(
					(async () => {
						try {
							setStatusLog(t("status.analyzingFrames"));
							const frames = await getFrameAnalysis(mediaFile.file);
							setFrameAnalysis(frames);
						} catch {
							// Frame analysis is optional
						}
					})()
				);
			}

			// MediaCapabilities check
			const videoStream = parsedMetadata.streams.find(s => s.codec_type === 'video');
			const audioStream = parsedMetadata.streams.find(s => s.codec_type === 'audio');
			if ('mediaCapabilities' in navigator) {
				additionalAnalyses.push(
					(async () => {
						try {
							const config: any = { type: 'file' as const };
							if (videoStream && videoStream.width && videoStream.height) {
								const framerate = videoStream.frame_rate
									? (() => {
										const parts = videoStream.frame_rate!.split('/');
										if (parts.length === 2) {
											const num = Number.parseFloat(parts[0]);
											const den = Number.parseFloat(parts[1]);
											return den > 0 ? num / den : 30;
										}
										return Number.parseFloat(videoStream.frame_rate!) || 30;
									})()
									: 30;
								config.video = {
									contentType: `video/${parsedMetadata.format.format_name.split(',')[0]}; codecs="${videoStream.codec_name}"`,
									width: videoStream.width,
									height: videoStream.height,
									bitrate: Number.parseInt(videoStream.bit_rate || '0') || 1000000,
									framerate,
								};
							}
							if (audioStream) {
								config.audio = {
									contentType: `audio/${parsedMetadata.format.format_name.split(',')[0]}; codecs="${audioStream.codec_name}"`,
									channels: String(audioStream.channels || 2),
									bitrate: Number.parseInt(audioStream.bit_rate || '0') || 128000,
									samplerate: Number.parseInt(audioStream.sample_rate || '48000'),
								};
							}
							if (config.video || config.audio) {
								const result = await navigator.mediaCapabilities.decodingInfo(config);
								setMediaCapability({
									supported: result.supported,
									smooth: result.smooth,
									powerEfficient: result.powerEfficient,
								});
							}
						} catch {
							// MediaCapabilities is optional
						}
					})()
				);
			}

			// Web Audio API analysis
			if (audioStream) {
				additionalAnalyses.push(
					(async () => {
						try {
							setStatusLog(t("status.analyzingAudio"));
							const audioContext = new AudioContext();
							const arrayBuffer = await mediaFile.file.arrayBuffer();
							const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

							let peakLevel = 0;
							let sumSquares = 0;
							let totalSamples = 0;

							for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
								const data = audioBuffer.getChannelData(channel);
								for (let i = 0; i < data.length; i++) {
									const abs = Math.abs(data[i]);
									if (abs > peakLevel) peakLevel = abs;
									sumSquares += data[i] * data[i];
								}
								totalSamples += data.length;
							}

							const rmsLevel = Math.sqrt(sumSquares / totalSamples);

							setAudioAnalysis({
								peakLevel,
								rmsLevel,
								duration: audioBuffer.duration,
								numberOfChannels: audioBuffer.numberOfChannels,
								sampleRate: audioBuffer.sampleRate,
							});

							await audioContext.close();
						} catch {
							// Audio analysis is optional - may fail for unsupported codecs
						}
					})()
				);
			}

			await Promise.allSettled(additionalAnalyses);
			setProgress(100);
			setStatusLog(t("status.analysisComplete"));

			announceSuccess(t("status.analysisCompleteFor", { name: mediaFile.name }));

		} catch (error) {
			let errorMessage = t("status.unknownError");
			
			if (error instanceof Error) {
				errorMessage = error.message;
			} else if (typeof error === 'string') {
				errorMessage = error;
			}
			
			setStatusLog(t("status.analysisFailed", { error: errorMessage }));
			setProgress(0);
			announceError(errorMessage);

		} finally {
			setIsAnalyzing(false);
			// Cleanup
			if (ffmpegInstance && isLoaded) {
				try {
					await ffmpegInstance.deleteFile(inputFileName);
				} catch {
					// Ignore cleanup errors
				}
			}
		}
	}, [mediaFile, ffmpegInstance, isLoaded, getFileMetadata, announce, announceSuccess, announceError]);

	// Format duration from seconds
	const formatDuration = (seconds: string | number | undefined): string => {
		if (!seconds) return t("unknown");
		const num = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
		if (isNaN(num)) return t("unknown");
		
		const hours = Math.floor(num / 3600);
		const minutes = Math.floor((num % 3600) / 60);
		const secs = Math.floor(num % 60);
		
		if (hours > 0) {
			return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
		}
		return `${minutes}:${secs.toString().padStart(2, '0')}`;
	};

	// Format file size
	const formatFileSize = (bytes: string | number): string => {
		const num = typeof bytes === 'string' ? parseInt(bytes) : bytes;
		if (isNaN(num)) return t("unknown");

		const units = ['B', 'KB', 'MB', 'GB'];
		let size = num;
		let unitIndex = 0;
		
		while (size >= 1024 && unitIndex < units.length - 1) {
			size /= 1024;
			unitIndex++;
		}
		
		return `${size.toFixed(2)} ${units[unitIndex]}`;
	};

	// Format bitrate
	const formatBitrate = (bitrate: string | undefined): string => {
		if (!bitrate) return t("unknown");
		const num = parseInt(bitrate);
		if (isNaN(num)) return bitrate;
		
		if (num >= 1000000) {
			return `${(num / 1000000).toFixed(1)} Mbps`;
		} else if (num >= 1000) {
			return `${(num / 1000).toFixed(0)} Kbps`;
		}
		return `${num} bps`;
	};

	const MediaIcon = mediaFile?.type === 'video' ? Video : Music;

	return (
		<ErrorBoundary>
			<div className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<Info className="h-8 w-8 me-4 text-primary" aria-hidden="true" />
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>

					{/* Status Bar */}
					<Alert className={`mb-6 ${isLoaded ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}>
						<AlertTriangle className="h-4 w-4" aria-hidden="true" />
						<AlertDescription className="font-bold">{statusLog}</AlertDescription>
					</Alert>

					{/* Main Interface */}
					<div className="flex flex-col gap-6">
						{/* 1. Upload Section */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Upload className="w-6 h-6" aria-hidden="true" />
									{t("steps.selectFile")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								<FileDropZone accept="video/*,audio/*" disabled={!isLoaded || isAnalyzing} onFiles={(files) => { const f = Array.from(files)[0]; if (f) handleFileChange({ target: { files } } as any); }} label={t("fileInput.dropLabel")} activeLabel={t("fileInput.activeLabel")} />
								<p className="mt-2 text-sm text-muted-foreground">
									{t("fileInput.hint")}
								</p>
								
								{/* Current File Info */}
								{mediaFile && (
									<div className="mt-4 p-4 border rounded-lg">
										<div className="flex items-center gap-3">
											<MediaIcon className="w-8 h-8 text-primary" />
											<div className="flex-1">
												<p className="font-medium">{mediaFile.name}</p>
												<p className="text-sm text-muted-foreground">
													{mediaFile.type.toUpperCase()} • {formatFileSize(mediaFile.size)} • {t("fileInput.modified", { date: new Date(mediaFile.file.lastModified).toLocaleString() })}
												</p>
											</div>
											<Badge variant="secondary" className="text-xs">
												{mediaFile.type}
											</Badge>
										</div>
									</div>
								)}
							</CardContent>
						</Card>

						{/* 2. Analyze Button */}
						{mediaFile && (
							<div className="flex justify-center">
								<Button
									onClick={analyzeMetadata}
									size="lg"
									variant={isLoaded && !isAnalyzing ? "default" : "outline"}
									disabled={!isLoaded || !mediaFile || isAnalyzing}
									className="px-8 py-4 text-lg"
								>
									{isAnalyzing ? (
										<>
											<Database className="w-5 h-5 me-3 animate-pulse" aria-hidden="true" />
											{t("analyze.analyzing")}
										</>
									) : (
										<>
											<Zap className="w-5 h-5 me-3" aria-hidden="true" />
											{t("analyze.analyzeButton")}
										</>
									)}
								</Button>
							</div>
						)}

						{/* Progress Bar */}
						{isAnalyzing && (
							<Card className="p-3 sm:p-6">
								<div className="flex justify-between items-center mb-4">
									<Badge variant="secondary" className="font-black text-lg">
										{t("analyze.progressLabel")}
									</Badge>
									<span className="text-lg font-black">{Math.round(progress)}%</span>
								</div>
								<Progress
									value={progress}
									className="mb-2"
									aria-label={t("analyze.progressAriaLabel", { percent: Math.round(progress) })}
								/>
							</Card>
						)}

						{/* 3. Results Section */}
						{metadata && (
							<>
								{/* File Format Information */}
								<Card className="p-3 sm:p-6">
									<CardHeader className="p-0 mb-4">
										<CardTitle className="flex items-center gap-3">
											<FileText className="w-6 h-6" aria-hidden="true" />
											{t("formatInfo.title")}
										</CardTitle>
									</CardHeader>
									<CardContent className="p-0">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div className="space-y-3">
												<div>
													<span className="text-sm text-muted-foreground">{t("formatInfo.format")}</span>
													<div className="font-bold">{metadata.format.format_long_name}</div>
													<div className="text-sm text-muted-foreground">{metadata.format.format_name}</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("formatInfo.duration")}</span>
													<div className="font-bold flex items-center gap-2">
														<Clock className="w-4 h-4" aria-hidden="true" />
														{formatDuration(metadata.format.duration)}
													</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("formatInfo.fileSize")}</span>
													<div className="font-bold">{formatFileSize(metadata.format.size || mediaFile?.size || 0)}</div>
												</div>
											</div>
											<div className="space-y-3">
												<div>
													<span className="text-sm text-muted-foreground">{t("formatInfo.bitrate")}</span>
													<div className="font-bold">{formatBitrate(metadata.format.bit_rate)}</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("formatInfo.streams")}</span>
													<div className="font-bold">{t("formatInfo.streamsValue", { count: metadata.format.nb_streams })}</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("formatInfo.startTime")}</span>
													<div className="font-bold">{metadata.format.start_time ? `${parseFloat(metadata.format.start_time).toFixed(3)}s` : "0.000s"}</div>
												</div>
												{metadata.format.nb_programs !== undefined && metadata.format.nb_programs > 0 && (
													<div>
														<span className="text-sm text-muted-foreground">{t("formatInfo.programs")}</span>
														<div className="font-bold">{metadata.format.nb_programs}</div>
													</div>
												)}
												{metadata.format.probe_score !== undefined && (
													<div>
														<span className="text-sm text-muted-foreground">{t("formatInfo.probeScore")}</span>
														<div className="font-bold">{t("formatInfo.probeScoreValue", { score: metadata.format.probe_score })}</div>
													</div>
												)}
											</div>
										</div>

										{/* Format Tags */}
										{metadata.format.tags && Object.keys(metadata.format.tags).length > 0 && (
											<div className="mt-6">
												<h4 className="font-semibold mb-3 flex items-center gap-2">
													<Tag className="w-4 h-4" aria-hidden="true" />
													{t("formatInfo.formatTags")}
												</h4>
												<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
													{Object.entries(metadata.format.tags).map(([key, value]) => (
														<div key={key} className="flex justify-between items-center p-2 bg-muted rounded">
															<span className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</span>
															<span className="text-sm text-muted-foreground">{value}</span>
														</div>
													))}
												</div>
											</div>
										)}
									</CardContent>
								</Card>

								{/* Streams Information */}
								<Card className="p-3 sm:p-6">
									<CardHeader className="p-0 mb-4">
										<CardTitle className="flex items-center gap-3">
											<Database className="w-6 h-6" aria-hidden="true" />
											{t("streamInfo.title")}
										</CardTitle>
									</CardHeader>
									<CardContent className="p-0">
										<div className="space-y-6">
											{metadata.streams.map((stream, index) => (
												<div key={index} className="border rounded-lg p-4">
													<div className="flex items-center gap-3 mb-4">
														{stream.codec_type === 'video' && <Monitor className="w-5 h-5 text-blue-500" />}
														{stream.codec_type === 'audio' && <Volume2 className="w-5 h-5 text-green-500" />}
														{stream.codec_type === 'subtitle' && <FileText className="w-5 h-5 text-purple-500" />}
														<Badge variant="secondary">
															{t("streamInfo.streamBadge", { index: stream.index, type: stream.codec_type.toUpperCase() })}
														</Badge>
													</div>

													<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
														<div>
															<span className="text-sm text-muted-foreground">{t("streamInfo.codec")}</span>
															<div className="font-bold">{stream.codec_long_name}</div>
															<div className="text-sm text-muted-foreground">{stream.codec_name}</div>
															{stream.codec_tag_string && stream.codec_tag_string !== '[0][0][0][0]' && (
																<div className="text-sm text-muted-foreground">{t("streamInfo.codecTag", { tagString: stream.codec_tag_string, tag: stream.codec_tag || "" })}</div>
															)}
														</div>

														{stream.codec_type === 'video' && (
															<>
																<div>
																	<span className="text-sm text-muted-foreground">{t("streamInfo.resolution")}</span>
																	<div className="font-bold">{stream.width}×{stream.height}</div>
																	{stream.coded_width && stream.coded_height &&
																		(stream.coded_width !== stream.width || stream.coded_height !== stream.height) && (
																		<div className="text-sm text-muted-foreground">{t("streamInfo.codedResolution", { width: stream.coded_width, height: stream.coded_height })}</div>
																	)}
																	{stream.pixel_format && (
																		<div className="text-sm text-muted-foreground">{stream.pixel_format}</div>
																	)}
																</div>
																<div>
																	<span className="text-sm text-muted-foreground">{t("streamInfo.frameRate")}</span>
																	<div className="font-bold">{stream.frame_rate || t("streamInfo.variable")}</div>
																	{stream.avg_frame_rate && stream.avg_frame_rate !== stream.frame_rate && (
																		<div className="text-sm text-muted-foreground">{t("streamInfo.avgFrameRate", { rate: stream.avg_frame_rate })}</div>
																	)}
																</div>
																{(stream.color_range || stream.color_primaries || stream.color_space || stream.color_transfer || stream.chroma_location) && (
																	<div>
																		<span className="text-sm text-muted-foreground">{t("streamInfo.color")}</span>
																		{stream.color_range && <div className="font-bold">{t("streamInfo.colorRange", { value: stream.color_range })}</div>}
																		{stream.color_space && <div className="text-sm text-muted-foreground">{t("streamInfo.colorSpace", { value: stream.color_space })}</div>}
																		{stream.color_transfer && <div className="text-sm text-muted-foreground">{t("streamInfo.colorTransfer", { value: stream.color_transfer })}</div>}
																		{stream.color_primaries && <div className="text-sm text-muted-foreground">{t("streamInfo.colorPrimaries", { value: stream.color_primaries })}</div>}
																		{stream.chroma_location && <div className="text-sm text-muted-foreground">{t("streamInfo.chromaLocation", { value: stream.chroma_location })}</div>}
																	</div>
																)}
																{(stream.has_b_frames !== undefined || stream.refs !== undefined) && (
																	<div>
																		<span className="text-sm text-muted-foreground">{t("streamInfo.encoding")}</span>
																		{stream.has_b_frames !== undefined && <div className="font-bold">{t("streamInfo.bFrames", { value: stream.has_b_frames })}</div>}
																		{stream.refs !== undefined && <div className="text-sm text-muted-foreground">{t("streamInfo.refFrames", { value: stream.refs })}</div>}
																		{stream.is_avc && <div className="text-sm text-muted-foreground">{t("streamInfo.avc", { value: stream.is_avc })}</div>}
																		{stream.nal_length_size && <div className="text-sm text-muted-foreground">{t("streamInfo.nalSize", { value: stream.nal_length_size })}</div>}
																	</div>
																)}
																{stream.bits_per_raw_sample && (
																	<div>
																		<span className="text-sm text-muted-foreground">{t("streamInfo.bitDepth")}</span>
																		<div className="font-bold">{t("streamInfo.bitDepthValue", { value: stream.bits_per_raw_sample })}</div>
																	</div>
																)}
																{stream.nb_frames && (
																	<div>
																		<span className="text-sm text-muted-foreground">{t("streamInfo.totalFrames")}</span>
																		<div className="font-bold">{parseInt(stream.nb_frames).toLocaleString()}</div>
																	</div>
																)}
																{stream.closed_captions !== undefined && stream.closed_captions > 0 && (
																	<div>
																		<span className="text-sm text-muted-foreground">{t("streamInfo.closedCaptions")}</span>
																		<div className="font-bold">{t("streamInfo.yes")}</div>
																	</div>
																)}
															</>
														)}

														{stream.codec_type === 'audio' && (
															<>
																<div>
																	<span className="text-sm text-muted-foreground">{t("streamInfo.channels")}</span>
																	<div className="font-bold">{stream.channels}{stream.channel_layout ? ` (${stream.channel_layout})` : ''}</div>
																</div>
																<div>
																	<span className="text-sm text-muted-foreground">{t("streamInfo.sampleRate")}</span>
																	<div className="font-bold">{stream.sample_rate ? `${parseInt(stream.sample_rate).toLocaleString()} Hz` : t("streamInfo.sampleRateUnknown")}</div>
																</div>
																{stream.sample_fmt && (
																	<div>
																		<span className="text-sm text-muted-foreground">{t("streamInfo.sampleFormat")}</span>
																		<div className="font-bold">{stream.sample_fmt}</div>
																	</div>
																)}
																{stream.bits_per_sample !== undefined && stream.bits_per_sample > 0 && (
																	<div>
																		<span className="text-sm text-muted-foreground">{t("streamInfo.bitsPerSample")}</span>
																		<div className="font-bold">{stream.bits_per_sample}</div>
																	</div>
																)}
																{stream.nb_frames && (
																	<div>
																		<span className="text-sm text-muted-foreground">{t("streamInfo.totalFrames")}</span>
																		<div className="font-bold">{parseInt(stream.nb_frames).toLocaleString()}</div>
																	</div>
																)}
															</>
														)}

														<div>
															<span className="text-sm text-muted-foreground">{t("streamInfo.bitrate")}</span>
															<div className="font-bold">{formatBitrate(stream.bit_rate)}</div>
														</div>

														<div>
															<span className="text-sm text-muted-foreground">{t("streamInfo.duration")}</span>
															<div className="font-bold">{formatDuration(stream.duration)}</div>
														</div>

														{stream.profile && (
															<div>
																<span className="text-sm text-muted-foreground">{t("streamInfo.profile")}</span>
																<div className="font-bold">{stream.profile}</div>
																{stream.level && (
																	<div className="text-sm text-muted-foreground">{t("streamInfo.level", { value: stream.level })}</div>
																)}
															</div>
														)}

														{stream.time_base && (
															<div>
																<span className="text-sm text-muted-foreground">{t("streamInfo.timeBase")}</span>
																<div className="font-bold">{stream.time_base}</div>
															</div>
														)}

														{stream.start_time && (
															<div>
																<span className="text-sm text-muted-foreground">{t("streamInfo.startTime")}</span>
																<div className="font-bold">{parseFloat(stream.start_time).toFixed(6)}s</div>
																{stream.start_pts !== undefined && (
																	<div className="text-sm text-muted-foreground">{t("streamInfo.startPts", { value: stream.start_pts })}</div>
																)}
															</div>
														)}

														{stream.duration_ts !== undefined && (
															<div>
																<span className="text-sm text-muted-foreground">{t("streamInfo.durationTimestamp")}</span>
																<div className="font-bold">{stream.duration_ts.toLocaleString()}</div>
															</div>
														)}
													</div>

													{/* Disposition Flags */}
													{stream.disposition && Object.entries(stream.disposition).some(([, v]) => v === 1) && (
														<div className="mt-4">
															<h5 className="font-medium mb-2">{t("streamInfo.disposition")}</h5>
															<div className="flex flex-wrap gap-2">
																{Object.entries(stream.disposition)
																	.filter(([, v]) => v === 1)
																	.map(([key]) => (
																		<Badge key={key} variant="outline" className="text-xs capitalize">
																			{key.replace(/_/g, ' ')}
																		</Badge>
																	))}
															</div>
														</div>
													)}

													{/* Stream Tags */}
													{stream.tags && Object.keys(stream.tags).length > 0 && (
														<div className="mt-4">
															<h5 className="font-medium mb-2">{t("streamInfo.streamTags")}</h5>
															<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
																{Object.entries(stream.tags).map(([key, value]) => (
																	<div key={key} className="flex justify-between items-center p-2 bg-muted/50 rounded text-sm">
																		<span className="font-medium capitalize">{key.replace(/_/g, ' ')}</span>
																		<span className="text-muted-foreground">{value}</span>
																	</div>
																))}
															</div>
														</div>
													)}
												</div>
											))}
										</div>
									</CardContent>
								</Card>

								{/* Video Preview */}
								{mediaFile?.type === 'video' && (
									<Card className="p-3 sm:p-6">
										<CardHeader className="p-0 mb-4">
											<CardTitle className="flex items-center gap-3">
												<Video className="w-6 h-6" aria-hidden="true" />
												{t("preview.title")}
											</CardTitle>
										</CardHeader>
										<CardContent className="p-0">
											<VideoPlayer
												videoUrl={mediaFile.url}
												fileName={mediaFile.name}
											/>
										</CardContent>
									</Card>
								)}

								{/* Chapters (if available) */}
								{metadata.chapters && metadata.chapters.length > 0 && (
									<Card className="p-3 sm:p-6">
										<CardHeader className="p-0 mb-4">
											<CardTitle className="flex items-center gap-3">
												<Calendar className="w-6 h-6" aria-hidden="true" />
												{t("chapters.title")}
											</CardTitle>
										</CardHeader>
										<CardContent className="p-0">
											<div className="space-y-3">
												{metadata.chapters.map((chapter, index) => (
													<div key={index} className="flex items-center justify-between p-3 border rounded">
														<div>
															<div className="font-medium">{t("chapters.chapterId", { id: chapter.id })}</div>
															{chapter.tags?.title && (
																<div className="text-sm text-muted-foreground">{chapter.tags.title}</div>
															)}
														</div>
														<div className="text-end">
															<div className="font-bold">{formatDuration(chapter.start_time)} - {formatDuration(chapter.end_time)}</div>
															<div className="text-sm text-muted-foreground">
																{t("chapters.chapterDuration", { duration: formatDuration(parseFloat(chapter.end_time) - parseFloat(chapter.start_time)) })}
															</div>
														</div>
													</div>
												))}
											</div>
										</CardContent>
									</Card>
								)}

								{/* Frame Analysis */}
								{frameAnalysis && frameAnalysis.frames.length > 0 && (
									<Card className="p-3 sm:p-6">
										<CardHeader className="p-0 mb-4">
											<CardTitle className="flex items-center gap-3">
												<Film className="w-6 h-6" aria-hidden="true" />
												{t("frameAnalysis.title")}
											</CardTitle>
										</CardHeader>
										<CardContent className="p-0">
											<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
												<div>
													<span className="text-sm text-muted-foreground">{t("frameAnalysis.totalFrames")}</span>
													<div className="font-bold">{frameAnalysis.nb_frames.toLocaleString()}</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("frameAnalysis.gopSize")}</span>
													<div className="font-bold">{frameAnalysis.gop_size}</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("frameAnalysis.avgFrameRate")}</span>
													<div className="font-bold">{t("frameAnalysis.avgFrameRateValue", { value: frameAnalysis.avg_frame_rate.toFixed(3) })}</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("frameAnalysis.duration")}</span>
													<div className="font-bold">{formatDuration(frameAnalysis.duration)}</div>
												</div>
											</div>

											{/* Frame type distribution */}
											{(() => {
												const typeCounts: Record<string, number> = {};
												let totalSize = 0;
												let minSize = Number.POSITIVE_INFINITY;
												let maxSize = 0;
												const typeSizes: Record<string, number[]> = {};

												for (const frame of frameAnalysis.frames) {
													const label = PICT_TYPE_LABELS[frame.pict_type] || `Type ${frame.pict_type}`;
													typeCounts[label] = (typeCounts[label] || 0) + 1;
													totalSize += frame.pkt_size;
													if (frame.pkt_size < minSize) minSize = frame.pkt_size;
													if (frame.pkt_size > maxSize) maxSize = frame.pkt_size;
													if (!typeSizes[label]) typeSizes[label] = [];
													typeSizes[label].push(frame.pkt_size);
												}

												const avgSize = totalSize / frameAnalysis.frames.length;

												return (
													<>
														<h4 className="font-semibold mb-3">{t("frameAnalysis.frameTypeDistribution")}</h4>
														<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6">
															{Object.entries(typeCounts)
																.sort(([a], [b]) => a.localeCompare(b))
																.map(([type, count]) => {
																	const pct = ((count / frameAnalysis.frames.length) * 100).toFixed(1);
																	const typeAvgSize = typeSizes[type].reduce((a, b) => a + b, 0) / typeSizes[type].length;
																	return (
																		<div key={type} className="p-3 border rounded-lg">
																			<div className="flex justify-between items-center mb-1">
																				<Badge variant="secondary" className="font-bold">{t("frameAnalysis.frameLabel", { type })}</Badge>
																				<span className="text-sm font-bold">{count.toLocaleString()} ({pct}%)</span>
																			</div>
																			<div className="text-sm text-muted-foreground">
																				{t("frameAnalysis.avgSize", { size: (typeAvgSize / 1024).toFixed(1) })}
																			</div>
																		</div>
																	);
																})}
														</div>

														<h4 className="font-semibold mb-3">{t("frameAnalysis.packetSizeStats")}</h4>
														<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
															<div>
																<span className="text-sm text-muted-foreground">{t("frameAnalysis.minPacketSize")}</span>
																<div className="font-bold">{t("frameAnalysis.packetSizeValue", { size: (minSize / 1024).toFixed(1) })}</div>
															</div>
															<div>
																<span className="text-sm text-muted-foreground">{t("frameAnalysis.avgPacketSize")}</span>
																<div className="font-bold">{t("frameAnalysis.packetSizeValue", { size: (avgSize / 1024).toFixed(1) })}</div>
															</div>
															<div>
																<span className="text-sm text-muted-foreground">{t("frameAnalysis.maxPacketSize")}</span>
																<div className="font-bold">{t("frameAnalysis.packetSizeValue", { size: (maxSize / 1024).toFixed(1) })}</div>
															</div>
														</div>
													</>
												);
											})()}
										</CardContent>
									</Card>
								)}

								{/* Audio Analysis */}
								{audioAnalysis && (
									<Card className="p-3 sm:p-6">
										<CardHeader className="p-0 mb-4">
											<CardTitle className="flex items-center gap-3">
												<Activity className="w-6 h-6" aria-hidden="true" />
												{t("audioAnalysis.title")}
											</CardTitle>
										</CardHeader>
										<CardContent className="p-0">
											<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
												<div>
													<span className="text-sm text-muted-foreground">{t("audioAnalysis.peakLevel")}</span>
													<div className="font-bold">{(20 * Math.log10(audioAnalysis.peakLevel)).toFixed(1)} dBFS</div>
													<div className="text-sm text-muted-foreground">{(audioAnalysis.peakLevel * 100).toFixed(1)}%</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("audioAnalysis.rmsLevel")}</span>
													<div className="font-bold">{(20 * Math.log10(audioAnalysis.rmsLevel)).toFixed(1)} dBFS</div>
													<div className="text-sm text-muted-foreground">{(audioAnalysis.rmsLevel * 100).toFixed(1)}%</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("audioAnalysis.dynamicRange")}</span>
													<div className="font-bold">{(20 * Math.log10(audioAnalysis.peakLevel / audioAnalysis.rmsLevel)).toFixed(1)} dB</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("audioAnalysis.decodedChannels")}</span>
													<div className="font-bold">{audioAnalysis.numberOfChannels}</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("audioAnalysis.decodedSampleRate")}</span>
													<div className="font-bold">{audioAnalysis.sampleRate.toLocaleString()} Hz</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("audioAnalysis.decodedDuration")}</span>
													<div className="font-bold">{formatDuration(audioAnalysis.duration)}</div>
												</div>
											</div>
										</CardContent>
									</Card>
								)}

								{/* Browser Compatibility */}
								{mediaCapability && (
									<Card className="p-3 sm:p-6">
										<CardHeader className="p-0 mb-4">
											<CardTitle className="flex items-center gap-3">
												<Cpu className="w-6 h-6" aria-hidden="true" />
												{t("browserCompat.title")}
											</CardTitle>
										</CardHeader>
										<CardContent className="p-0">
											<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
												<div>
													<span className="text-sm text-muted-foreground">{t("browserCompat.supported")}</span>
													<div className="font-bold flex items-center gap-2">
														<span className={`inline-block w-3 h-3 rounded-full ${mediaCapability.supported ? 'bg-green-500' : 'bg-red-500'}`} />
														{mediaCapability.supported ? t("browserCompat.yes") : t("browserCompat.no")}
													</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("browserCompat.smoothPlayback")}</span>
													<div className="font-bold flex items-center gap-2">
														<span className={`inline-block w-3 h-3 rounded-full ${mediaCapability.smooth ? 'bg-green-500' : 'bg-yellow-500'}`} />
														{mediaCapability.smooth ? t("browserCompat.yes") : t("browserCompat.no")}
													</div>
												</div>
												<div>
													<span className="text-sm text-muted-foreground">{t("browserCompat.powerEfficient")}</span>
													<div className="font-bold flex items-center gap-2">
														<span className={`inline-block w-3 h-3 rounded-full ${mediaCapability.powerEfficient ? 'bg-green-500' : 'bg-yellow-500'}`} />
														{mediaCapability.powerEfficient ? t("browserCompat.yes") : t("browserCompat.no")}
													</div>
												</div>
											</div>
										</CardContent>
									</Card>
								)}
							</>
						)}
					</div>

					{/* Information */}
					<Alert className="mt-8">
						<AlertTriangle className="h-4 w-4 text-green-600" aria-hidden="true" />
						<AlertDescription className="font-bold">
							{t("privacy")}
						</AlertDescription>
					</Alert>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default Metadata;